import React, { useEffect, useState, useRef, useContext } from 'react';
import { useSelector } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';
import { useInView } from 'react-intersection-observer';

import { IKImage } from 'imagekitio-react';
import { Button, Modal } from 'react-bootstrap';
import LinesEllipsis from 'react-lines-ellipsis';

import InfiniteScroll from '../InfiniteScroll';
import { GatingContext, WidgetsContext } from '../widgets/core';
import Content from '../widgets/core/content';
import { AnalyticsDebug } from './Analytics';

import { AssetContentViewer } from './viewer';
import { getContentForFieldSelector } from './fields';

import styles from './assetviewer.module.scss';


export function AssetViewer({ show, initialContent, asset, contentGrid, onActiveAssetChange, onClose }) {
  const scrollContainerRef = React.useRef(null);
  const scrollPosition = React.useRef(0);
  const touchPosition = React.useRef(0);

  const context = React.useContext(WidgetsContext);
  const contentContext = React.useContext(Content.Context);

  const [activeAsset, setActiveAsset] = React.useState();
  const [fullscreen, setFullscreen] = React.useState(false);

  const gatingContext = React.useContext(GatingContext);

  const [state, setState] = useState({
    position: 0,
    visible: true,
    fixed: false,
    scrollDisabled: false,
    selected: {},
    preloadedAssets: []
  });

  const domain = context.domain;

  const contentPage = useSelector(state => {
    var mappings = [];

    if (initialContent) {
      mappings = [initialContent];
    }

    if (!contentGrid) {
      return {
        items: mappings,
        loading: false,
        currentPage: 0,
        totalPages: 0,
        itemsPerPage: 0
      };
    }

    const widgets = _.get(state, `${context.selector}.widgets`) || [];
    const contentGridWidget = widgets.find(item => item.uid === contentGrid);

    const items = _.get(contentGridWidget, 'content.mappings.items') || [];
    mappings = [...mappings, ...items];
    mappings = _.uniqBy(mappings, 'uid');

    const loading = _.get(contentGridWidget, 'content.mappings.loading', false);
    const currentPage = _.get(contentGridWidget, 'content.mappings.pagination.current', 0);
    const totalPages = _.get(contentGridWidget, 'content.mappings.pagination.totalPages', 1);
    const itemsPerPage = _.get(contentGridWidget, 'properties.desktop.layoutStyle.itemsPerPage', 2);

    return {
      items: mappings,
      loading: loading,
      currentPage: currentPage,
      totalPages: totalPages,
      itemsPerPage: itemsPerPage
    };
  });

  const handleWheelEvent = _.throttle((wheelEvent) => {
    const scrollContainer = scrollContainerRef.current.dialog.querySelector(`.${styles.assetViewerContent}`);

    const previousPosition = scrollPosition.current;
    const currentPosition = scrollContainer.scrollTop;

    if (wheelEvent.deltaX) {
      scrollPosition.current = scrollContainer.scrollTop;
      return;
    }

    if (previousPosition > currentPosition) {
      scrollContainer.classList.add(styles.headerVisible);
    } else {
      scrollContainer.classList.remove(styles.headerVisible);
    }

    scrollPosition.current = scrollContainer.scrollTop;
  }, 100);

  const handleTouchStart = (e) => {
    touchPosition.current = e.touches[0].clientY;
  }

  const handleTouchEnd = (e) => {
    const scrollContainer = scrollContainerRef.current.dialog.querySelector(`.${styles.assetViewerContent}`);
    var currentPosition = e.changedTouches[0].clientY;
    const previousPosition = touchPosition.current;

    const diff = currentPosition - previousPosition;

    if (diff > 0) {
      scrollContainer.classList.add(styles.headerVisible);
    } else {
      scrollContainer.classList.remove(styles.headerVisible);
    }
  }

  useEffect(() => {
    if (show) {
      setState(state => {
        return {
          ...state,
          selected: asset
        }
      })

      var scrollContent = document.getElementsByClassName('modal-content');

      scrollContent = _.head(scrollContent);
      scrollContent.addEventListener('wheel', handleWheelEvent);
      scrollContent.addEventListener('touchstart', handleTouchStart);
      scrollContent.addEventListener('touchend', handleTouchEnd);

      return (() => {
        scrollContent.removeEventListener('wheel', handleWheelEvent);
        scrollContent.removeEventListener('touchstart', handleTouchStart);
        scrollContent.removeEventListener('touchend', handleTouchEnd);
      })
    }

  }, [show, asset]);

  useEffect(() => {
    if (activeAsset) {
      contentContext.onContentChange(contentGrid, activeAsset);
    }

  }, [activeAsset]);

  const fetchAdditionalContent = () => {
    const nextPage = contentPage.currentPage + 1;
    if (nextPage >= contentPage.totalPages) {
      return;
    }

    contentContext.fetchMappingsForContentGrid(contentGrid, nextPage, contentPage.itemsPerPage);
  }

  const handleAssetSelection = (content) => {
    setState(state => {
      return {
        ...state,
        selected: content
      }
    });

    switch (content.type) {
      case 'INTERACTIVE_CONTENT':
      case 'LANDING_PAGE':
      case 'RESOURCE_CENTER': {
        if (!content.url) {
          return;
        }

        const pageUrl = `${domain}${content.url}`;
        window.open(pageUrl);
        break;
      }
      case 'ASSET': {
        scrollToAsset(content.uid);
        break;
      }
    }
  }

  const scrollToAsset = (id) => {
    const scrollContainer = scrollContainerRef.current.dialog.querySelector(`.${styles.assetViewerContent}`);
    const assetContainer = scrollContainer.querySelector(`[data-asset='${id}']`);

    if (!assetContainer) {
      return;
    }

    const scrollTop = scrollContainer.scrollTop;
    const assetOffset = assetContainer.offsetTop;

    const scrollDown = assetOffset > scrollTop;
    const offset = scrollDown ? 7 : 7;

    scrollContainer.scrollTo({
      top: assetContainer.offsetTop + offset,
      behavior: "smooth"
    });
  }

  const handleAssetVisibilityChange = (asset, visible) => {
    const prevId = _.get(activeAsset, 'asset.id');
    const nextId = _.get(asset, 'id');


    if (prevId == nextId && !visible) {
      setActiveAsset(null);
      return;
    }

    if (visible) {
      setActiveAsset(asset);
    }
  }

  const handleAssetLoaded = (asset) => {
    setState((state) => {
      const assets = (contentPage.items || []).filter(item => item.type === 'ASSET').map(item => item.uid);
      const activeIndex = assets.indexOf(asset);
      const nextIndex = activeIndex + 1;
      const nextAsset = assets[nextIndex];

      var preloadedAssets = _.get(state, 'preloadedAssets') || [];
      preloadedAssets = [...preloadedAssets, asset];

      if (nextAsset) {
        preloadedAssets = [...preloadedAssets, nextAsset];
      }

      preloadedAssets = _.uniq(preloadedAssets);

      return {
        ...state,
        preloadedAssets: preloadedAssets
      }
    })
  }

  const toggleFullScreen = () => {
    setFullscreen(state => {
      return !state
    });
  }

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  }

  return (
    <Modal
      ref={scrollContainerRef}
      backdrop
      className={styles.assetViewerModal}
      contentClassName={cx(styles.assetViewerContent, styles.headerVisible, {
        [styles.fullscreen]: fullscreen
      })}
      dialogClassName={cx(styles.assetViewerDialog, {
        [styles.fullscreen]: fullscreen
      })}
      fullscreen={fullscreen}
      backdropClassName={styles.backdropWrapper}
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      enforceFocus={false}
    >
      <Modal.Body className={styles.assetViewerBody}>
        <ContentList
          contents={contentPage.items}
          loading={contentPage.loading}
          fetchAssets={fetchAdditionalContent}
          selected={_.get(activeAsset, 'uid')}
          onSelection={handleAssetSelection}
        />
        <div className={styles.assetsContainer}>
          {(contentPage.items || []).filter(item => item.type === 'ASSET').map((asset, index) => {

            const active = asset.uid == _.get(activeAsset, 'uid') && !gatingContext.gating;
            const preload = state.preloadedAssets.find(item => item === asset.uid) != null;

            return (
              <AssetView
                key={asset.uid}
                asset={asset}
                preload={active || preload}
                active={active}
                fullscreen={fullscreen}
                onLoad={handleAssetLoaded}
                onVisibilityChange={handleAssetVisibilityChange}
                toggleFullScreen={toggleFullScreen}
                onClose={handleClose}
              />
            )
          })}
        </div>
      </Modal.Body>
    </Modal>
  )
}

const ContentCard = ({ asset, selected, onClick }) => {
  const ref = React.useRef(null);

  const name = getContentForFieldSelector(asset, 'name') || "";
  const icon = getContentForFieldSelector(asset, 'icon');
  const iconUrl = _.get(icon, 'url') || '/public/cleverstory/v2/static/default_card.jpg';

  useEffect(() => {
    if (selected) {
      scrollIntoView();
    }
  }, [selected])

  const handleClick = (asset) => {
    onClick(asset);
  }

  const scrollIntoView = () => {
    if (!ref.current) {
      return;
    }

    const scrollContainer = ref.current.parentNode;
    const parentWidth = scrollContainer.clientWidth;
    const scrollLeft = scrollContainer.scrollLeft;

    const offset = ref.current.offsetLeft;
    const cardWidth = ref.current.clientWidth;

    if (offset <= scrollLeft) {
      scrollTo(scrollContainer, offset - 25, 500);
    }

    if (offset + cardWidth > scrollLeft + parentWidth) {
      scrollTo(scrollContainer, offset + cardWidth + 25 - parentWidth, 500);
    }
  }

  const scrollTo = (element, to, duration) => {
    var start = element.scrollLeft,
      change = to - start,
      currentTime = 0,
      increment = 20;

    var animateScroll = function () {
      var easeInOutQuad = function (t, b, c, d) {
        t /= d / 2;
        if (t < 1) return c / 2 * t * t + b;
        t--;
        return -c / 2 * (t * (t - 2) - 1) + b;
      };

      currentTime += increment;
      var val = easeInOutQuad(currentTime, start, change, duration);
      element.scrollLeft = val;
      if (currentTime < duration) {
        setTimeout(animateScroll, increment);
      }
    };

    animateScroll();
  }

  return (
    <div ref={ref} key={asset.uid} className={cx(styles.contentCard, {
      [styles.selected]: selected
    })} onClick={() => handleClick(asset)}>
      <div className={cx(styles.icon, {
        [styles.selected]: selected
      })}
      >
        <IKImage path={iconUrl} width={140} height={160} />
        <div className={styles.borders}></div>
      </div>
      <div className={styles.details}>
        <LinesEllipsis
          className={styles.name}
          text={name}
          maxLine={2}
          basedOn='letters'
          ellipsis={'...'}
        />
        <ContentMetadata className={styles.additionalData} content={asset} />
      </div>
    </div>
  );
}

const ContentCardPlaceholder = () => {
  return (
    <div className={cx(styles.contentCard, styles.placeholder)}>
      <div className={cx(styles.icon)}></div>
      <div className={styles.details}>
        <div className={styles.name}>
          <span>&nbsp;</span>
          <span>&nbsp;</span>
        </div>
        <div className={cx(styles.assetMetadata)}>
          {_.times(3).map(index => {
            return (
              <div key={index} className={styles.metadata}><span>&nbsp;</span></div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const ContentMetadata = ({ content, className }) => {
  const contentType = content.type;
  const type = getContentForFieldSelector(content, 'contentType');
  const pages = _.get(content, 'content.metadata.pages');
  const fileSize = getContentForFieldSelector(content, 'fileSize');

  return (
    <div className={cx(styles.contentMetadata, className)}>
      <div className={styles.metadata}>{type}</div>
      {pages &&
        <div className={styles.metadata}>{pages} Pages</div>
      }
      {contentType === 'ASSET' && type != 'LINK' &&
        <div className={styles.metadata}>{fileSize}</div>
      }
    </div>
  );
}

const ContentList = ({ contents, selected, loading, fetchAssets, onSelection }) => {
  const listRef = React.useRef(null);
  const horizontalContentList = React.useRef(null);

  const [canShowLeftArrow, setCanShowLeftArrow] = useState(false);
  const [canShowRightArrow, setCanShowRightArrow] = useState(false);
  const [scrolledPosition, setScrolledPosition] = useState(0);
  const [hovered, setHovered] = useState(false);

  const handleCardClick = (content) => {
    onSelection(content);
  }

  useEffect(() => {
    calculateShowLeftArrow()
    calculateShowRightArrow()
  }, [scrolledPosition, contents])

  const handleScroll = _.debounce((element, direction) => {
    const position = element.scrollLeft;
    if (direction === "left") {
      scrollTo(element, position - 250, 150);
    }
    if (direction === "right") {
      scrollTo(element, position + 250, 150)
    }
    setScrolledPosition(position);
  }, 150);

  const scrollTo = (element, to, duration) => {
    var start = element.scrollLeft,
      change = to - start,
      currentTime = 0,
      increment = 20;
    var animateScroll = function () {
      var easeInOutQuad = function (t, b, c, d) {
        t /= d / 2;
        if (t < 1) return c / 2 * t * t + b;
        t--;
        return -c / 2 * (t * (t - 2) - 1) + b;
      };
      currentTime += increment;
      var val = easeInOutQuad(currentTime, start, change, duration);
      element.scrollLeft = val;
      if (currentTime < duration) {
        setTimeout(animateScroll, increment);
      }
    };
    animateScroll();
  }

  useEffect(() => {
    var element = horizontalContentList.current;
    element.addEventListener("scroll", () => handleScroll(element));
    handleScroll(element);
    return () => {
      element.removeEventListener("scroll", () => handleScroll(element));
    };
  }, []);

  const calculateShowLeftArrow = () => {
    if (scrolledPosition != 0) {
      setCanShowLeftArrow(true)
    } else {
      setCanShowLeftArrow(false)
    }
  }

  const calculateShowRightArrow = () => {
    var element = horizontalContentList.current;
    var scrollWidth = element.scrollWidth;
    var clientWidth = element.clientWidth;
    var offsetWidth = element.offsetWidth;

    if (scrolledPosition > scrollWidth) {
      setCanShowRightArrow(false);
    }
    if (scrolledPosition === (scrollWidth - clientWidth)) {
      setCanShowRightArrow(false)
    }
    if (scrolledPosition !== (scrollWidth - clientWidth)) {
      setCanShowRightArrow(true)
    }
    if ((scrollWidth - Math.round(scrolledPosition)) === offsetWidth) {
      setCanShowRightArrow(false)
    }
  }

  const handleClick = (direction) => {
    var element = horizontalContentList.current;
    handleScroll(element, direction);
  }

  return (
    <div ref={listRef} className={cx(styles.contentList)} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <span onClick={() => handleClick("left")} className={cx(styles.arrows, styles.leftArrow,{ [styles.showLeftArrow]: (canShowLeftArrow && hovered)})}>
        <i className={cx('cleverstory-drop-down-left')} />
      </span>
      <div className={cx(styles.cardsContainer)} ref={horizontalContentList}>
        <InfiniteScroll direction='horizontal' loading={loading} loadData={fetchAssets}>
          {
            contents && contents.map((asset, index) => {
              const assetSelected = _.get(asset, 'uid') === selected;
              return (
                <ContentCard
                  key={asset.uid}
                  asset={asset}
                  selected={assetSelected}
                  onClick={(content) => handleCardClick(content)}
                />
              )
            })
          }

          {loading && _.times(3).map(index => {
            return <ContentCardPlaceholder key={index} />
          })}
        </InfiniteScroll>
      </div>
      <span onClick={() => handleClick("right")} className={cx(styles.arrows, styles.rightArrow,{ [styles.showRightArrow]: (canShowRightArrow && hovered)})}>
        <i className={cx('cleverstory-drop-down-right')} />
      </span>
    </div>
  )
}

const AssetView = ({
  asset, preload, active, fullscreen,
  onLoad, onVisibilityChange, toggleFullScreen, onClose
}) => {

  const contentContext = React.useContext(Content.Context);
  const gatingContext = React.useContext(GatingContext);
  const widgetContext = React.useContext(WidgetsContext);

  const [initialised, setInitialised] = React.useState(preload);
  const [analytics, setAnalytics] = React.useState(null);

  const activeSession = useSelector(state => _.get(state, `content.analytics.${asset.id}.item.id`));
  const disableDownload = _.get(asset, 'content.disableDownload', false);

  const [activeAssetRef] = useDelayedInView({
    rootMargin: '-50% 0% -50% 0%',
    root: document.querySelector(`.${styles.assetViewerDialog}`),
    onChange: (visible) => {
      onVisibilityChange(asset, visible);
    }
  });

  const [gatingConfigurations, setGatingConfigurations] = useState(_.get(asset, 'content.gatingConfigurations', []));

  useEffect(() => {
      const updatedItems = gatingConfigurations.filter(({id}) => !gatingContext.assetConfigurations.includes(id));
      setGatingConfigurations(updatedItems);
  }, [gatingContext.assetConfigurations])

  const [preloadAssetRef] = useDelayedInView({
    rootMargin: '-50% 0% -50% 0%',
    root: document.querySelector(`.${styles.assetViewerContent}`),
    skip: initialised,
    onChange: (visible) => {
      if (visible) {
        setInitialised(true);
      }
    }
  });

  const setRefs = React.useCallback(
    (node) => {
      activeAssetRef(node)
      preloadAssetRef(node)
    },
    [activeAssetRef, preloadAssetRef]
  );

  useEffect(() => {
    if (preload) {
      setInitialised(true);
    }
  }, [preload]);

  const handleViewerLoaded = () => {
    if(onLoad){
      onLoad(asset.uid);
    }
  }

  const handleDownload = (asset) => {
    if(disableDownload) {
      return;
    }

    const downloadUrl = _.get(asset, 'content.asset.downloadUrl');
    window.open(downloadUrl, "_blank");

    contentContext.onDownload(asset);
  }

  useEffect(() => {
    if(gatingContext.configuration) {
      return;
    }
    if(analytics) {
      triggerAssetGating(analytics)
    }
  }, [analytics])

  const triggerAssetGating = ({ duration, percentageRead }) => {
    if (gatingContext.preview) {
      return;
    }

    gatingConfigurations.every(item => {
      if(!item.enabled) {
        return true;
      }

      var triggerValue = _.get(item, 'trigger.value', 0);
      var limit = duration;

      if (_.get(item, 'trigger.type', '') === "PERCENTAGE") {
        limit = percentageRead;
      }

      if (triggerValue <= limit) {
        var gating = _.get(item, 'gating.id', null);

        if (gating) {
          widgetContext.fetchGating(gating);
          gatingContext.triggerGating(_.get(item, 'trigger.type', ''), null, item);
          return false;
        }
      }

      return true;
    })
  }

  return (
    <div ref={setRefs} className={styles.asset} data-asset={asset.uid}>
      <AssetHeader
        asset={asset}
        analytics={analytics}
        fullscreen={fullscreen}
        toggleFullScreen={toggleFullScreen}
        canDownload={!disableDownload}
        onDownload={handleDownload}
        onClose={onClose}
        debug={contentContext.debug}
      />
      <div className={cx(styles.body, {
        [styles.initialised]: initialised
      })}>
        {initialised &&
          <AssetContentViewer
            asset={asset}
            active={active}
            onLoad={handleViewerLoaded}
            onAnalyticsChange={(analytics) => {
              if (activeSession || active) {
                setAnalytics(analytics);
                contentContext.onAnalyticsChange(asset, analytics);
              }
            }}
            onDebugAnalytics={(analytics) => {
              if (contentContext.debug) {
                setAnalytics(analytics);
              }
            }}
          />
        }
      </div>
    </div>
  )
}

const AssetHeader = ({ asset, analytics, fullscreen, toggleFullScreen, canDownload, onDownload, onClose, debug }) => {
  const name = getContentForFieldSelector(asset, 'name');
  const downloadUrl = _.get(asset, 'content.asset.downloadUrl');
  const externalVideoProvider = _.get(asset, 'content.asset.metadata.external_video_provider')

  return (
    <div className={styles.header}>
      <div className={styles.details}>
        <div className={styles.name}>{name}</div>
        <ContentMetadata content={asset} />
      </div>
      <div className={styles.actions}>
        {debug &&
          <Button className={cx(styles.action)}>
            <AnalyticsDebug asset={asset.content.asset} analytics={analytics} />
          </Button>
        }
        {downloadUrl && canDownload && externalVideoProvider != "youtube" && externalVideoProvider != "vimeo" && externalVideoProvider != "wistia" &&
          <Button className={cx(styles.action, styles.download)} onClick={() => { onDownload(asset) }}>
            <i className={cx('cleverstory-download')} />
            <span>DOWNLOAD</span>
          </Button>
        }
        <Button className={cx(styles.action, styles.fullscreen)} onClick={toggleFullScreen}>
          <i className={`cleverstory-${fullscreen ? 'minimize-editor' : 'expand-editor'}`} />
        </Button>
        <Button className={cx(styles.action, styles.close)} onClick={onClose}>
          <i className={cx('cleverstory-close')} />
        </Button>
      </div>
    </div>
  )
}

const useDelayedInView = ({ root, rootMargin, threshold, skip, onChange }) => {
  const timeoutId = React.useRef(null);

  const [ref, visible, entry] = useInView({
    root: root,
    rootMargin: rootMargin,
    threshold: threshold,
    skip: skip
  });

  const intersectionRatio = _.get(entry, 'intersectionRatio');

  useEffect(() => {
    const isIntersecting = _.get(entry, 'isIntersecting');
    const intersectionRatio = _.get(entry, 'intersectionRatio');

    if (isIntersecting || intersectionRatio > 0) {
      _delayElementEnteredCheck();
    } else {
      _handleElementExited()
    }
  }, [entry, intersectionRatio]);

  const _delayElementEnteredCheck = () => {
    if (timeoutId.current) {
      return;
    }

    timeoutId.current = setTimeout(() => {
      _handleElementEntered();
    }, 500);
  }

  const _handleElementEntered = () => {
    onChange(true);

    _clearTimeout();
  }

  const _handleElementExited = () => {
    _clearTimeout();
  }

  const _clearTimeout = () => {
    if (!timeoutId.current) {
      return;
    }

    clearTimeout(timeoutId.current);
    timeoutId.current = null;
  }

  return [ref, visible];
}
